import React from 'react';
import { CodeOutlined } from '@ant-design/icons';
import { getFeatureFlagValue } from '@citrite/feature-flags';
import { Api, UserRole } from '@citrite/sf-api';
import { getLanguage } from '@citrite/translate';
import {
	type EntitlementsContentParams,
	entitlementsPiletBanners,
	entitlementsPiletComponents,
	entitlementsPiletModals,
	type InstallContentParams,
} from '@sharefiledev/entitlements-pilet-sdk';
import type {
	OverlayModalProps,
	PiletApi,
	PiletUserContext,
} from '@sharefiledev/sharefile-appshell';
import { addBlocks } from './blocks';
import {
	AsyncPaywallBanner,
	AsyncPaywallModal,
	AsyncSolutionInstallModal,
} from './components';
import { EmptyModal } from './components/EmptyModal';
import type { UserEmailInfo } from './components/types';
import {
	AsyncZoneMigrationPaywall,
	AsyncZoneMigrationPaywallModal,
} from './components/ZoneMigrationPaywall';
import { addBlockDevelopment } from './development';
import { FeatureFlag } from './featureFlags';
import { setLogger } from './logger';
import { AsyncDevelopmentPage } from './pages';
import type { DebugEntitlementsContentParams } from './pages/Development/DevelopmentPageUtils';
import { PiralTestStrings } from './piralTestStrings';
import { getDefaultModalProps } from './utils/componentUtils';

const knownSmokeTestCustomers = [
	'pilet-tests.sharefiletest.com',
	'pilet-tests.sharefilestaging.com',
	'pilet-tests.sharefile.com',
];

export function setup(piletApi: PiletApi) {
	setLogger(piletApi.sf.getLogger());
	piletApi.sf.registerSfApiConfigHandler(config => Api.configure(config as any));
	addBlocks(piletApi);
	const context: PiletUserContext = piletApi.sf.piletUserContext.get();
	const validUserId = context.user.Id !== undefined && context.user.Id !== null;
	const isFreeTrial = context.account.IsFreeTrial ?? true;
	const userRID = `sharefile:account-${context.account.Id}/user-${context.user.Id}`;
	const userEmailInfo: UserEmailInfo = {
		userEmail: context.user.Email!,
		userFirstName: context.user.FirstName!,
		userLastName: context.user.LastName!,
	};
	let isClient = context.user.Roles?.includes(UserRole.Client) ?? true;
	const isSelfServiceEnabled =
		!context.account.Preferences?.DisableAdminBillingSectionInApp;
	if (context.user.Roles?.includes(UserRole.Employee)) {
		isClient = false;
	}
	const locale = getLanguage();
	let isAdministrator = false;
	if (
		context.user.Roles?.includes(UserRole.AdminBilling) &&
		context.user.Roles?.includes(UserRole.AdminChangePlan)
	) {
		isAdministrator = true;
	}
	if (isAdministrator) {
		piletApi.registerModal<InstallContentParams>(
			entitlementsPiletModals.solutionInstallModal,
			({ completeInteraction, cancelInteraction, options }) => (
				<AsyncSolutionInstallModal
					completeInteraction={completeInteraction}
					cancelInteraction={cancelInteraction}
					locale={locale}
					{...options!}
				/>
			),
			{
				modalProps: {
					width: 640,
					footer: null,
					styles: { content: { padding: 0, borderRadius: 12 } },
				},
			}
		);
	}
	if (isClient || !validUserId) {
		piletApi.registerModal(
			entitlementsPiletModals.entitlementsModal,
			({ ...cancelInteraction }) => <EmptyModal {...cancelInteraction} />,
			{
				modalProps: {
					width: 0,
					footer: null,
				},
			}
		);
		piletApi.registerExtension(entitlementsPiletBanners.entitlementsBanner, ({}) => null);
	} else {
		piletApi.registerModal<EntitlementsContentParams>(
			entitlementsPiletModals.entitlementsModal,
			({ completeInteraction, cancelInteraction, options, updateModalProps }) => {
				return (
					<AsyncPaywallModal
						piletApi={piletApi}
						entitlementsContent={options!}
						isAdmin={isAdministrator}
						isFreeTrial={isFreeTrial}
						locale={locale}
						userRID={userRID}
						userEmailInfo={userEmailInfo}
						completeInteraction={completeInteraction}
						cancelInteraction={cancelInteraction}
						updateModalProps={updateModalProps}
					/>
				);
			},
			{
				modalProps: getDefaultModalProps() as OverlayModalProps,
			}
		);
		piletApi.registerExtension<EntitlementsContentParams>(
			entitlementsPiletBanners.entitlementsBanner,
			({ params }) => (
				<AsyncPaywallBanner
					piletApi={piletApi}
					entitlementsContent={params}
					isFreeTrial={isFreeTrial}
					isAdmin={isAdministrator}
					locale={locale}
					userRID={userRID}
					userEmailInfo={userEmailInfo}
				/>
			)
		);
	}
	piletApi.registerModal<EntitlementsContentParams>(
		entitlementsPiletModals.zoneMigrationModal,
		({ cancelInteraction, updateModalProps, options }) => (
			<AsyncZoneMigrationPaywallModal
				isSelfServiceEnabled={isSelfServiceEnabled}
				completeInteraction={navigateToManagePlanPage}
				cancelInteraction={cancelInteraction}
				updateModalProps={updateModalProps}
				{...options!}
			/>
		),
		{
			// @ts-ignore app-shell types need to be updated
			modalProps: { ...getDefaultModalProps() },
		}
	);
	piletApi.registerExtension<EntitlementsContentParams>(
		entitlementsPiletComponents.zoneMigration,
		({ params }) => <AsyncZoneMigrationPaywall supportsSelfService={false} {...params!} />
	);
	if (isTestPageEnabled()) {
		// eslint-disable-next-line no-console
		console.log('Adding entitlements test page');
		addBlockDevelopment(piletApi);
		piletApi.registerPage('/entitlements', () => (
			<AsyncDevelopmentPage
				piletApi={piletApi}
				isAdmin={isAdministrator}
				isFreeTrial={isFreeTrial}
				userRID={userRID}
				userEmailInfo={userEmailInfo}
			/>
		));
		piletApi.sf.registerLeftNavComponent({
			href: '/entitlements',
			title: () => 'Entitlements Page',
			weight: 200,
			icon: () => <CodeOutlined style={{ fontSize: '24px' }} />,
		});
		piletApi.registerExtension<DebugEntitlementsContentParams>(
			PiralTestStrings.customBanner,
			({ params }) => (
				<AsyncPaywallBanner
					piletApi={piletApi}
					entitlementsContent={params}
					isFreeTrial={params.isTrial ?? false}
					isAdmin={params.isAdministrator ?? false}
					userRID={userRID}
					userEmailInfo={userEmailInfo}
					locale={params.locale}
					customContentResponse={params.bannerContentResponse}
				/>
			)
		);

		piletApi.registerModal<DebugEntitlementsContentParams>(
			PiralTestStrings.customModal,
			({ completeInteraction, cancelInteraction, options, updateModalProps }) => {
				return (
					<AsyncPaywallModal
						piletApi={piletApi}
						entitlementsContent={options!}
						isAdmin={options?.isAdministrator ?? false}
						isFreeTrial={options?.isTrial ?? false}
						locale={options?.locale ?? locale}
						userRID={options?.userRIDOverride ?? userRID}
						userEmailInfo={userEmailInfo}
						completeInteraction={completeInteraction}
						cancelInteraction={cancelInteraction}
						customContentResponse={options?.modalContentResponse}
						updateModalProps={updateModalProps}
					/>
				);
			},
			{
				modalProps: getDefaultModalProps() as OverlayModalProps,
			}
		);
	}
}

function navigateToManagePlanPage() {
	const managePlanRoute = '/settings/admin/billing/manageplan';
	window.location.assign(managePlanRoute);
}

function isTestPageEnabled(): boolean {
	if (
		process.env.NODE_ENV === 'development' ||
		knownSmokeTestCustomers.includes(location.hostname)
	) {
		return true;
	}
	try {
		return getFeatureFlagValue(FeatureFlag.EnablePLGTestPage) ?? false;
	} catch (Error) {
		console.error(Error);
	}
	return false;
}
